import {
  Box,
  Container,
  GlobalStyles,
  Grid,
  Typography
} from "@mui/material";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { Reveal } from "src/theme/animation";
import HeroImageCarousel from "src/theme/HeroImageCarousel";
import HeroSection from "src/theme/HeroSection";
import Logo from "src/theme/Logo";
import HowItWorksSection from "../components/HowItWorksSection";

const DEFAULT_IMAGES = [
  "/images/peer-peer.png",
  "/images/athlete-athlete.png",
  "/images/youth-guardian.png",
  "/images/ai-peer.png",
] as const;

const PILLARS = [
  { label: "Confidence.", colorKey: "info" },
  { label: "Composure.", colorKey: "success" },
  { label: "Character.", colorKey: "warning" },
] as const;

const isScreenshotsParamOn = (value: string | null, present: boolean): boolean => {
  if (!present) return false;
  const normalized = (value ?? "").trim().toLowerCase();
  return normalized !== "0" && normalized !== "false" && normalized !== "off";
};

const SECTION_REVEAL_SX = { mt: 3, mb: 2 } as const;

const HowItWorksPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const showScreenshots = isScreenshotsParamOn(
    searchParams.get("screenshots"),
    searchParams.has("screenshots"),
  );

  const toggleScreenshots = () => {
    const next = new URLSearchParams(searchParams);
    if (showScreenshots) {
      next.delete("screenshots");
    } else {
      next.set("screenshots", "1");
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <Box>
      <GlobalStyles
        styles={{
          "@page": {
            marginTop: "0.5in",
            marginBottom: "0.5in",
            marginLeft: 0,
            marginRight: 0,
          },
          "@page :first": {
            margin: 0,
          },
          ".avoid-break": {
            breakInside: "avoid",
            pageBreakInside: "avoid",

            breakAfter: "avoid",
            pageBreakAfter: "avoid",

            breakBefore: "avoid",
            pageBreakBefore: "avoid",
          },
          "@media print": {
            ".noprint": {
              display: "none",
            },
            "#main-footer": {
              display: "none",
            },
            "html, body": {
              margin: "0 !important",
              padding: "0 !important",
            },
            "main, #layout-container": {
              margin: "0 !important",
              padding: "0 !important",
              minHeight: "auto !important",
            },
            ".how-it-works-page .MuiContainer-root": {
              margin: "0 !important",
              padding: "0 !important",
              maxWidth: "100% !important",
            },
            ".how-it-works-hero": {
              minHeight: "25vh !important",
            },
          },
        }}
      />
      <HeroSection
        className="how-it-works-hero"
        image={<HeroImageCarousel images={DEFAULT_IMAGES} />}
        minHeight="40vh"
        showWave={true}
        animateHero
      >
        <Container className="how-it-works-page" maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container direction="column" alignItems="flex-start"
            justifyContent="space-between" height="100%" spacing={1}>
            <Reveal variant="fadeUp" trigger="mount">
              <Box>
                <Logo height={80} variant="dark" />
              </Box>
            </Reveal>
            <Reveal variant="fadeUp" trigger="mount">
              <Typography
                variant="h6"
                component="p"
                sx={{ color: "rgba(255,255,255,0.92)", fontWeight: 400, lineHeight: 1.6, mb: 3, maxWidth: 720 }}
              >
                The human-centered personal development platform designed to make growth visible, measurable, and transferable.
              </Typography>
            </Reveal>
            <Reveal variant="fadeUp" trigger="mount">
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: { xs: 2, sm: 3 } }}>
                {PILLARS.map((pillar) => (
                  <Box key={pillar.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={(theme) => ({
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: theme.palette[pillar.colorKey].main,
                        boxShadow: `0 0 8px ${theme.palette[pillar.colorKey].main}`,
                      })}
                    />
                    <Typography sx={{ color: "white", fontWeight: 800, letterSpacing: 0.4 }}>
                      {pillar.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Reveal>
          </Grid>
        </Container>
      </HeroSection>

      <Container maxWidth="lg" >

        <HowItWorksSection />

        <Box data-label="Contact" className="avoid-break" sx={SECTION_REVEAL_SX}>
          <Reveal variant="fadeUp">
            <Box
              sx={{
                textAlign: "center",
                py: 2,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                strongmindstrongperformance.com • info@strongmindstrongperformance.com
              </Typography>
            </Box>
          </Reveal>
        </Box>

      </Container>
    </Box>
  );
};

export default HowItWorksPage;
